/** @jsx jsx */
import React, { useEffect, useState, useCallback } from "react";

import { jsx } from "@emotion/core";

import { Typography, Upload, Icon, message, Input } from "antd";

import { Formik, Form, ErrorMessage } from "formik";

//Hooks
import useProfile from "../../hooks/profile/useProfile";
import useAPI from "../../helperHooks/useAPI";
import useRedirect from "../../helperHooks/useRedirect";
import useEligibilityMetaData from "../../hooks/auth/useEligibilityMetaData";

import {
  UpdateProfileRequest,
  GetProfileDetailsResponse,
  UpdateProfilePayload
} from "../../services/profileService/ProfileService.type";
import profileService from "../../services/profileService/ProfileService";

import { profileStyles } from "./ProfileStyles";

import {
  ProfileContents as CONSTANTS,
  ProfileValidationSchema,
  GstinStatusPair,
  ImageFileConstants
} from "./ProfileConstants";

import FormMultiInputTags, {
  MultiTags
} from "../../components/formHelpers/FormMultiInputTags";
import { FormInput, FormDropdown } from "../../components/formHelpers";
import CustomButton from "../../components/button";
import { validateFile } from "../../helper/FileValidator";
import { GstinValidityService } from "../../services/gstinValidityService";
import { DownloadServiceResponse } from "../../services/downloadService/DownloadService.type";
import { DownloadService } from "../../services/downloadService";

export type UpdateProfileState = UpdateProfilePayload & {
  gstinField: string;
  existingProfileImages: string | null;
  formattedKeyCustomers: MultiTags[];
  formattedKeySuppliers: MultiTags[];
  currentCustomer: string;
  currentSupplier: string;
  imageUrl: string | null;
  profileGstins: string[];
};
const formatResponse = (data?: string[]): MultiTags[] =>
  data?.map((item, index) => ({
    id: index,
    value: item,
    removed: false,
    loading: false
  })) || [];
const getInitialValues = (
  profileDetails: GetProfileDetailsResponse
): UpdateProfileState => ({
  ...profileDetails,
  gstinField: "",
  currentCustomer: "",
  currentSupplier: "",
  existingProfileImages: profileDetails.imageUrl,
  formattedKeyCustomers: formatResponse(profileDetails.keyCustomers),
  formattedKeySuppliers: formatResponse(profileDetails.keySuppliers),
  profileImages: null,
  profileGstins: profileDetails.registeredGstins.map(item => item.gstin)
});

const Profile: React.FC = () => {
  const {
    updateProfile,
    profileState: { loading }
  } = useProfile();

  const {
    eligibilityMetaData: { gstin }
  } = useEligibilityMetaData();

  const redirect = useRedirect();

  const [addingGstin, setAddingGstin] = useState(false);
  const { callAPI: getProfileDetails, ...profileData } = useAPI(
    profileService.getProfileDetails
  );

  const { response } = profileData;
  const { callAPI: downloadDocument } = useAPI(
    DownloadService.downloadEligibilityDocument
  );
  const [imageUrl, setImageUrl] = useState<string>();
  const toBase64 = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setImageUrl(reader.result as string);
    };
  };
  /**
   * to get the "key" which is to be updated by formik
   */
  const getPath = (listName: string, index: number) => `${listName}[${index}]`;
  const formatMultiTagsPayload = (list: MultiTags[]) =>
    list
      .filter(item => !item.removed && !item.error)
      .map(item => item.value.toUpperCase());
  const updateProfileOnSubmit = (values: UpdateProfileState) => {
    const updatePayload: UpdateProfileRequest = {
      data: {
        eaNumber: values.eaNumber,
        pfNumber: values.pfNumber,
        activeGstNumber: values.activeGstNumber,
        registeredGstins: values.registeredGstins,
        accountNumber: values.accountNumber,
        bankName: values.bankName,
        ifscCode: values.ifscCode,
        keyCustomers: formatMultiTagsPayload(values.formattedKeyCustomers),
        keySuppliers: formatMultiTagsPayload(values.formattedKeySuppliers),
        profileImages: values.profileImages
      },
      onSuccess: () => {
        //TODO: instead of calling this API again, update it from response of update API
        loadProfileDetails();
      },
      redirect,
      changeActiveGstin: values.activeGstNumber !== gstin
    };
    updateProfile(updatePayload);
  };
  const loadProfileDetails = useCallback(() => {
    getProfileDetails(
      {},
      (profileDetailsResponse?: GetProfileDetailsResponse) => {
        if (profileDetailsResponse?.s3Key) {
          downloadDocument(
            profileDetailsResponse.s3Key,
            (downloadReponse?: DownloadServiceResponse) => {
              setImageUrl(downloadReponse?.downloadUrl);
            }
          );
        }
      }
    );
  }, [getProfileDetails, downloadDocument]);
  useEffect(() => {
    loadProfileDetails();
  }, [loadProfileDetails]);

  const renderProfileForm = (
    <div css={profileStyles} className="mb-4">
      <Typography className="title my-4 mb-10">
        {CONSTANTS.titles.heading}
      </Typography>
      {response && (
        <Formik
          initialValues={getInitialValues(response)}
          onSubmit={updateProfileOnSubmit}
          validationSchema={ProfileValidationSchema(response?.activeGstNumber)}
          //To allow fetch details on update
          enableReinitialize
          validateOnChange
        >
          {({ values, setFieldValue, errors, handleChange, setFieldError }) => {
            const handleAddGstin = async () => {
              if (!errors.gstinField && values.gstinField) {
                const length = values.registeredGstins.length;
                try {
                  setAddingGstin(true);
                  await GstinValidityService.checkGstinRegisterable(
                    values.gstinField
                  );
                  setAddingGstin(false);
                  values.registeredGstins.push({
                    gstin: values.gstinField,
                    isEnabled: true
                  });
                  setFieldValue(getPath("registeredGstins", length), {
                    gstin: values.gstinField,
                    isEnabled: true
                  });
                  setFieldValue("gstinField", "");
                } catch (error) {
                  setAddingGstin(false);
                  setFieldError("gstinField", error.message || error);
                }
              }
            };
            return (
              <React.Fragment>
                <Form>
                  <div className="grid-container">
                    <FormInput
                      name="eaNumber"
                      label={CONSTANTS.labels.eaNumber}
                      placeholder={CONSTANTS.placeholders.eaNumber}
                    />
                    <FormInput
                      name="pfNumber"
                      label={CONSTANTS.labels.pfNumber}
                      placeholder={CONSTANTS.placeholders.pfNumber}
                    />
                    <div className="keyCustomer-container">
                      <FormMultiInputTags
                        name="currentCustomer"
                        listName="formattedKeyCustomers"
                        label={CONSTANTS.labels.keyCustomers}
                        listItems={values.formattedKeyCustomers}
                        onAdd={GstinValidityService.checkForValidity}
                      />
                    </div>
                    <div className="keySupplier-container">
                      <FormMultiInputTags
                        name="currentSupplier"
                        listName="formattedKeySuppliers"
                        label={CONSTANTS.labels.keySuppliers}
                        listItems={values.formattedKeySuppliers}
                        onAdd={GstinValidityService.checkForValidity}
                      />
                    </div>
                    <FormInput
                      name="accountNumber"
                      label={CONSTANTS.labels.currentAccount}
                      placeholder={CONSTANTS.placeholders.accountNumber}
                    />
                    <FormInput
                      name="bankName"
                      label={CONSTANTS.labels.bankName}
                      placeholder={CONSTANTS.placeholders.bankName}
                    />
                    {values.registeredGstins && (
                      <FormDropdown
                        name="activeGstNumber"
                        label={CONSTANTS.labels.activeGst}
                        options={values.profileGstins}
                        loading={loading}
                      />
                    )}
                    <FormInput
                      name="ifscCode"
                      label={CONSTANTS.labels.ifscCode}
                      placeholder={CONSTANTS.placeholders.ifscCode}
                    />
                    <div className="added-gstin-container">
                      <label
                        className="d-block"
                        htmlFor="added-gstin-container"
                      >
                        {CONSTANTS.labels.addUpdateGstin}
                      </label>
                      <Input
                        name="gstinField"
                        className="gstinField"
                        onChange={handleChange}
                        value={values.gstinField}
                        placeholder={CONSTANTS.placeholders.gstin}
                        disabled={addingGstin}
                        onPressEnter={e => {
                          e.preventDefault();
                          handleAddGstin();
                        }}
                        suffix={
                          addingGstin ? (
                            <Icon type="loading" />
                          ) : (
                            <Icon
                              type="plus-circle"
                              onClick={() => {
                                handleAddGstin();
                              }}
                            />
                          )
                        }
                      />

                      {errors.gstinField && (
                        <Typography className="error">
                          {errors.gstinField}
                        </Typography>
                      )}

                      <div className="added-gstin-header mt-3">
                        {CONSTANTS.labels.addedGstin}
                      </div>
                      {values.registeredGstins &&
                        values.registeredGstins.map((item: GstinStatusPair) => {
                          return (
                            <div className="mt-2 pt-2" key={item.gstin}>
                              {item.gstin}
                            </div>
                          );
                        })}
                    </div>
                    <div className="upload-profile-container">
                      <Upload
                        name={"profileImages"}
                        accept={ImageFileConstants.acceptableFileExtensions.join(
                          ","
                        )}
                        beforeUpload={file => {
                          const fileInvalid = validateFile(
                            file,
                            ImageFileConstants
                          );
                          if (fileInvalid) {
                            message.error(fileInvalid);
                          }
                          if (!fileInvalid) {
                            setFieldValue("profileImages", file);
                            toBase64(file);
                          }
                          return false;
                        }}
                        showUploadList={false}
                      >
                        {CONSTANTS.buttons.uploadImage}
                        <Icon type="upload" className="upload-icon" />
                      </Upload>
                      {imageUrl && (
                        <img
                          src={imageUrl}
                          alt="profile_image"
                          className="mt-3"
                        ></img>
                      )}
                      <ErrorMessage name="profileImages">
                        {errorMessage => {
                          return (
                            <Typography className="error">
                              {errorMessage}
                            </Typography>
                          );
                        }}
                      </ErrorMessage>
                    </div>
                  </div>
                  <CustomButton
                    htmlType="submit"
                    containerDivClassName="d-flex justify-content-md-end mt-3"
                    type="primary"
                    loading={loading}
                    disabled={loading}
                  >
                    {CONSTANTS.buttons.update}
                  </CustomButton>
                </Form>
              </React.Fragment>
            );
          }}
        </Formik>
      )}
    </div>
  );
  return renderProfileForm;
};

export default Profile;
